/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import { compose, result } from 'underscore';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Dialog, DialogType, DialogFooter } from '@bingads-webui-react/fabric-control';
import { openOldUI, getFallbackPath } from '@bingads-webui-react/fallback-feature';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { Scenario, Activity } from '@bingads-webui-react/hoc-with-signal';
import { PrimaryButton, DefaultButton } from '@bingads-webui-react/fabric-with-signal';
import { getNewUiWelcomeStyles } from '@bingads-webui-campaign/ui-next-style';
import { isTestEnvironment } from '@bingads-webui-campaign/global-app-init';

const NewUINavigationPreferenceKey = 'WebApp.Next.LandingPage';

// a blocking non-draggable fabric dialog to set newUI preference
class NewUIPreferenceDialogRaw extends React.PureComponent {
  constructor(props) {
    super(props);

    const {
      permissions,
      preferenceService,
      instrumentation,
    } = props;

    const isTestEnv = isTestEnvironment(document.domain);

    if (result(permissions.dynamic, 'LegacyUIDeprecation', false) || isTestEnv) {
      this.state = {
        showDialog: false,
      };

      return;
    }

    this.preferenceSetLogger = instrumentation.newUINavigation.create().preferenceSet.create();
    // explicit start and stop signal
    this.preferenceSetLogger.startSignal();

    this.preferenceService = preferenceService;
    const newUIPreference = preferenceService.findByNameAtUserLevel(NewUINavigationPreferenceKey);
    const isLandOnNewUIEnabledPilot = permissions.IsLandOnNewUIEnabled;
    const showOnlyOKButton = isLandOnNewUIEnabledPilot && (!newUIPreference || newUIPreference.Data !== 'NewUI');

    const phase2PilotEnabled = permissions.dynamic.NewWebUI;

    this.state = {
      showDialog: showOnlyOKButton || ((phase2PilotEnabled === true) && (newUIPreference === undefined)),
      showOnlyOKButton,
    };

    // silently set the preference to NewUI if the preference is already set and it's not NewUI
    // so server would serve new UI next time when user logs in
    // because newUIPreference is not undefined, there would be no dialog for user to set preference
    if (newUIPreference && newUIPreference.Data !== 'NewUI') {
      this.setPreferenceAndLog('NewUI');
    }
  }

  setPreferenceAndLog = (preference) => {
    if (preference) {
      this.preferenceService.setAtUserLevel({
        Name: NewUINavigationPreferenceKey,
        Data: preference,
      });
    }

    this.preferenceSetLogger.trace({
      preference: preference || 'decide later',
    });
  };

  selectNewUI = () => {
    this.setPreferenceAndLog('NewUI');
    this.dismissDialog('NewUI');
  }

  selectOldUI = () => {
    this.setPreferenceAndLog('OldUI');
    this.dismissDialog('OldUI');

    const returnPath = getFallbackPath(this.props.scope, null, null);
    openOldUI(this.props.scope, returnPath);
  }

  decideLater = () => {
    this.setPreferenceAndLog();
    this.dismissDialog();
  }

  learnMore = () => {
    this.setPreferenceAndLog('NewUI');
    this.dismissDialog('NewUI');

    if (this.helpTopicSpan) {
      this.helpTopicSpan.click();
    }
  }

  dismissDialog = (userSelection) => {
    this.setState({
      showDialog: false,
    });

    // in the case user clicks x button to dismiss the dialog
    // userSelection is an event object from base-button which needs special handling
    if (typeof userSelection !== 'string') {
      if (this.state.showOnlyOKButton) {
        this.setPreferenceAndLog('NewUI');
        userSelection = 'dismiss by x button with pilot NewUIAsDefault enabled';
      } else {
        userSelection = 'decide later by x button';
      }
    } else if (!userSelection) {
      userSelection = 'decide later';
    }

    this.preferenceSetLogger.stopSignal(userSelection);
  }

  render() {
    const {
      i18n,
      classes,
    } = this.props;

    return (
      <Scenario name="NewUIPreferenceDialog">
        <Activity name="SetPreferenceAction">
          <Dialog
            hidden={!this.state.showDialog}
            onDismiss={this.dismissDialog}
            className={classes.newUINavigationPreferenceDialog}
            dialogContentProps={{
              type: DialogType.close, // with x close button in the upper-right corner
            }}
            maxWidth={460}
            minWidth={460}
            modalProps={{
              isBlocking: true,
              dragOptions: undefined, // non-dragable
            }}
          >
            <div
              className="customContentWrapper custom-content-wrapper"
            >
              <div className="customContentTitle title">
                {i18n.getString(_TL_('We have liftoff of our redesign!'))}
              </div>
              <div
                className="customContentText custom-content-text"
              >
                {`${i18n.getString(_TL_('From campaigns to billing—and everything in between—we\'ve redesigned all of Microsoft Advertising so that it\'s easier for you to use.  '))}`}
                <span
                  query="help:app54708"
                  className="HelpTopic hiddenSpan"
                  ref={(span) => {
                    this.helpTopicSpan = span;
                  }}
                />
                <button
                  className="btn-link"
                  aria-label="Learn what's new"
                  onClick={this.learnMore}
                  onKeyPress={this.learnMore}
                  tabIndex={0}
                >
                  {`${i18n.getString(_TL_('Learn what\'s new'))}`}
                </button>
                <br />
                <br />
                {`${i18n.getString(_TL_('We hope you enjoy our new look. In case you need to go back to the previous version, you can switch for a limited time by going to '))}`}
                <b>
                  {`${i18n.getString(_TL_('Tools > Return to previous Microsoft Advertising.'))}`}
                </b>
              </div>
              <DialogFooter>
                <DefaultButton
                  className="selectNewUI lets-go"
                  onClick={this.selectNewUI}
                  text={`${i18n.getString(_TL_('Let\'s go!'))}`}
                />
                {!this.state.showOnlyOKButton &&
                <div>
                  <PrimaryButton
                    className="decideLater"
                    onClick={this.decideLater}
                    text={`${i18n.getString(_TL_('Remind me later'))}`}
                  />
                  <PrimaryButton
                    className="selectOldUI"
                    onClick={this.selectOldUI}
                    text={`${i18n.getString(_TL_('Not now'))}`}
                  />
                </div>
                }
              </DialogFooter>
            </div>
          </Dialog>
        </Activity>
      </Scenario>
    );
  }
}

NewUIPreferenceDialogRaw.propTypes = {
  permissions: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  classes: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  instrumentation: PropTypes.object.isRequired,
};

export const NewUIPreferenceDialog = compose(
  WrappedComponent => withDefaultStyles(WrappedComponent, getNewUiWelcomeStyles),
  withInstrumentation,
  withScope,
  withI18nConsumer,
  withPreference,
  withPermissions
)(NewUIPreferenceDialogRaw);
